import { GoogleGenerativeAI } from "@google/generative-ai";
import BackendService from './BackendService';

class GeminiService {
  constructor(apiKey) {
    // Comprehensive API key validation
    if (!apiKey) {
      console.error("GeminiService: No API key provided");
      throw new Error("Gemini API Key is required");
    }

    // Trim and validate key format
    const trimmedKey = apiKey.trim();
    if (trimmedKey.length === 0) {
      console.error("GeminiService: API key is an empty string after trimming");
      throw new Error("Invalid API key: Empty string");
    }

    try {
      // Initialize Gemini and Backend services
      this.genAI = new GoogleGenerativeAI(trimmedKey);
      this.backendService = new BackendService();
      console.log("GeminiService: Successfully initialized with API key");
    } catch (error) {
      console.error("GeminiService Initialization Error:", {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw new Error(`Failed to initialize Gemini Service: ${error.message}`);
    }
  }

  // Supported languages with their codes and language-specific instructions
  static LANGUAGES = {
    'English': {
      code: 'en',
      instruction: 'Respond in clear, professional English.'
    },
    'Hindi': {
      code: 'hi',
      instruction: 'सरल और स्पष्ट हिंदी में उत्तर दें। (Respond in simple and clear Hindi.)'
    },
    'Gujarati': {
      code: 'gu',
      instruction: 'સ્પષ્ટ અને સરળ ગુજરાતી માં જવાબ આપો. (Respond in clear and simple Gujarati.)'
    }
  };

  async formatResponse(text, type) {
    // Detect content type if not provided
    if (!type) {
      type = this.detectContentType(text);
    }

    switch (type) {
      case 'bullet':
        return text.split('*').map(item => item.trim()).filter(Boolean)
          .map(item => `• ${item}`).join('\n');
      
      case 'qa':
        return text.replace(/Q:|Question:/g, '**Question:**')
          .replace(/A:|Answer:/g, '**Answer:**');
      
      case 'essay':
        const paragraphs = text.split('\n\n');
        return `### ${paragraphs[0]}\n\n${paragraphs.slice(1).join('\n\n')}`;
      
      case 'experiment':
        return text.replace(/(Aim|Materials|Procedure|Observation|Conclusion):/g, '**$1:**');
      
      case 'math':
        return text.replace(/(Problem|Solution|Step \d+):/g, '**$1:**')
          .replace(/\$([^$]+)\$/g, '`$1`');
      
      default:
        return text;
    }
  }

  detectContentType(text) {
    if (text.match(/^\s*[\*•-]\s/m)) return 'bullet';
    if (text.match(/Q:|Question:|A:|Answer:/)) return 'qa';
    if (text.match(/(Aim|Materials|Procedure|Observation|Conclusion):/)) return 'experiment';
    if (text.match(/(Problem|Solution|Step \d+):/)) return 'math';
    if (text.length > 200 && text.split('\n\n').length > 1) return 'essay';
    return 'default';
  }

  async generateResponse(prompt, settings = {}) {
    try {
      // Validate prompt
      if (!prompt || typeof prompt !== 'string') {
        throw new Error("Invalid prompt: Must be a non-empty string");
      }

      // First, try to find a response in the custom database
      let databaseResponse = null;
      try {
        databaseResponse = await this.backendService.searchDatabase(prompt);
      } catch (error) {
        console.error("Error searching custom database:", error);
      }

      // Extract settings with defaults
      const { 
        standard = 'Not specified', 
        subject = 'Not specified', 
        method = 'Not specified', 
        language = 'English' 
      } = settings;

      // Validate language
      const languageConfig = GeminiService.LANGUAGES[language] || GeminiService.LANGUAGES['English'];
      
      // Select model
      // const model = this.genAI.getGenerativeModel({ model: "gemini-pro" });
      // const model = this.genAI.getGenerativeModel({ model: "gemini-1.5-pro" });
      const model = this.genAI.getGenerativeModel({ model: "gemini-2.0-flash" });
      
      // Build contextual prompt
      let contextualPrompt = `
        Context:
        - Standard: ${standard}
        - Subject: ${subject}
        - Method: ${method}

        Language Instruction: 
        ${languageConfig.instruction}
      `;

      // If database response exists, include it in the context
      if (databaseResponse) {
        contextualPrompt += `
        Custom Database Context:
        ${databaseResponse.content}
        Source: ${databaseResponse.source}

        Please use the above context to help formulate your response.
        `;
      }

      contextualPrompt += `\nUser Prompt: ${prompt}`;
      
      console.log("Generating content with prompt:", contextualPrompt);
      
      // Generate content
      const result = await model.generateContent(contextualPrompt);
      const response = await result.response;
      
      const responseText = response.text();
      console.log("Generated response:", responseText);

      // Format the response based on content type
      const formattedResponse = await this.formatResponse(responseText);
      
      // Add source information
      const source = databaseResponse ? 
        `\n\n---\n*Source: Custom Database - ${databaseResponse.source}*` :
        '\n\n---\n*Source: Gemini AI*';

      if (!databaseResponse && formattedResponse.length > 100) {
        try {
          const fileName = `${prompt.slice(0, 50)}_${new Date().toISOString()}.txt`;
          const content = `Question: ${prompt}\nAnswer: ${formattedResponse}`;
          await this.backendService.saveToDatabase(content, fileName);
        } catch (error) {
          console.error("Error saving to database:", error);
        }
      }
      
      return formattedResponse + source;
    } catch (error) {
      // Detailed error logging
      console.error("Gemini API Generation Error:", {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack,
        fullError: JSON.stringify(error, Object.getOwnPropertyNames(error))
      });

      // Specific error handling
      const errorMessage = error.message || '';
      
      if (errorMessage.includes('API key not valid')) {
        return "Error: The provided API key is invalid. Please check your configuration.";
      }
      if (errorMessage.includes('network')) {
        return "Network error: Unable to connect to Gemini API";
      }
      if (errorMessage.includes('quota')) {
        return "Error: API usage quota exceeded";
      }
      
      return `Unexpected error: ${errorMessage}`;
    }
  }

  // Method to get supported languages
  getSupportedLanguages() {
    return Object.keys(GeminiService.LANGUAGES);
  }
}

export default GeminiService;
